import { useState, useEffect } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { supabase } from '@/lib/supabase';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Copy, ChevronLeft } from 'lucide-react';

function CopyBlock({ code, language = '' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-background/80 border opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent"
        title="Copy to clipboard"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
    </div>
  );
}

function ParamTable({ params }: { params: { name: string; type: string; required: boolean; default?: string; desc: string }[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 pr-4 font-medium">Parameter</th>
            <th className="text-left py-2 pr-4 font-medium">Type</th>
            <th className="text-left py-2 pr-4 font-medium">Required</th>
            <th className="text-left py-2 font-medium">Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <tr key={p.name} className="border-b border-muted">
              <td className="py-2 pr-4 font-mono text-primary">{p.name}</td>
              <td className="py-2 pr-4 text-muted-foreground">{p.type}</td>
              <td className="py-2 pr-4">{p.required ? '✅' : p.default ? `Default: ${p.default}` : '—'}</td>
              <td className="py-2">{p.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function McpDocs() {
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();

  }, []);

  // Scroll to hash on mount
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, []);

  const handleSearch = (query: string) => {
    if (query?.trim()) {
      navigate(`/saved?query=${encodeURIComponent(query.trim())}`);
      return;
    }
    navigate('/saved');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onSearch={handleSearch} onSignOut={handleSignOut} />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Breadcrumb */}
        <Link to="/docs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ChevronLeft className="h-4 w-4" />
          Back to Docs
        </Link>

        <h1 className="text-4xl font-bold mb-2">MCP Server</h1>
        <p className="text-muted-foreground text-lg mb-2">
          Give your AI agents access to your personal knowledge base.
        </p>
        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground mb-10">
          <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 font-mono">https://mcp.solem.ai/mcp</span>
          <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5">Streamable HTTP</span>
          <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5">Bearer Auth</span>
        </div>

        {/* TOC */}
        <nav className="rounded-xl border bg-card p-5 mb-12">
          <h2 className="font-semibold mb-3">On this page</h2>
          <ul className="space-y-1.5 text-sm">
            <li><a href="#getting-your-api-key" className="text-muted-foreground hover:text-primary">Getting Your API Key</a></li>
            <li><a href="#connecting-your-agent" className="text-muted-foreground hover:text-primary">Connecting Your Agent</a></li>
            <li>
              <a href="#tools-reference" className="text-muted-foreground hover:text-primary">Tools Reference</a>
              <ul className="ml-4 mt-1 space-y-1">
                <li><a href="#search" className="text-muted-foreground hover:text-primary">search</a></li>
                <li><a href="#list_recent" className="text-muted-foreground hover:text-primary">list_recent</a></li>
                <li><a href="#get" className="text-muted-foreground hover:text-primary">get</a></li>
                <li><a href="#save" className="text-muted-foreground hover:text-primary">save</a></li>
                <li><a href="#update" className="text-muted-foreground hover:text-primary">update</a></li>
                <li><a href="#delete" className="text-muted-foreground hover:text-primary">delete</a></li>
              </ul>
            </li>
            <li><a href="#workflows" className="text-muted-foreground hover:text-primary">Workflows & Best Practices</a></li>
            <li><a href="#rate-limits" className="text-muted-foreground hover:text-primary">Rate Limits</a></li>
            <li><a href="#errors" className="text-muted-foreground hover:text-primary">Error Handling</a></li>
          </ul>
        </nav>

        {/* ========== GETTING API KEY ========== */}
        <section id="getting-your-api-key" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold mb-4">Getting Your API Key</h2>
          <p className="mb-4">
            Your human needs to create an API key before you can access their Solem knowledge base.
          </p>
          <div className="rounded-xl border bg-card p-5 mb-4">
            <h3 className="font-semibold mb-3">Setup steps</h3>
            <ol className="list-decimal pl-5 space-y-2 text-sm">
              <li>Go to <a href="/settings" className="text-primary hover:underline font-medium">Settings</a></li>
              <li>Under <strong>API Keys</strong>, click <strong>Create API Key</strong></li>
              <li>Give it a name (e.g. "Claude Desktop", "Cursor", "OpenClaw")</li>
              <li><strong>Copy the key immediately</strong> — it's only shown once</li>
            </ol>
          </div>
          <p className="text-sm text-muted-foreground">
            Keys look like <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">sk_solem_a1b2c3d4e5...</code> · Max 5 active keys per account · Revoke unused keys from Settings
          </p>
        </section>

        {/* ========== CONNECTING ========== */}
        <section id="connecting-your-agent" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold mb-4">Connecting Your Agent</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Claude Desktop / Cursor</h3>
              <CopyBlock code={`{
  "mcpServers": {
    "solem": {
      "command": "npx",
      "args": [
        "-y", "mcp-remote",
        "https://mcp.solem.ai/mcp",
        "--header", "Authorization: Bearer YOUR_API_KEY"
      ]
    }
  }
}`} />
            </div>

            <div>
              <h3 className="font-semibold mb-2">Claude Code</h3>
              <CopyBlock code={`claude mcp add solem -- npx -y mcp-remote https://mcp.solem.ai/mcp --header "Authorization: Bearer YOUR_API_KEY"`} />
            </div>

            <div>
              <h3 className="font-semibold mb-2">OpenClaw</h3>
              <CopyBlock code={`{
  "mcpServers": {
    "solem": {
      "url": "https://mcp.solem.ai/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`} />
            </div>

            <div>
              <h3 className="font-semibold mb-2">Any MCP-compatible agent</h3>
              <p className="text-sm text-muted-foreground">
                POST to <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">https://mcp.solem.ai/mcp</code> with header <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">Authorization: Bearer sk_solem_...</code> · Streamable HTTP transport · Stateless
              </p>
            </div>
          </div>
        </section>

        {/* ========== TOOLS REFERENCE ========== */}
        <section id="tools-reference" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold mb-6">Tools Reference</h2>

          {/* SEARCH */}
          <div id="search" className="mb-12 scroll-mt-20">
            <h3 className="text-xl font-semibold mb-1 font-mono">search</h3>
            <p className="text-muted-foreground mb-4">
              Search saved pages by keyword. Uses full-text search across titles, descriptions, notes, and content.
            </p>
            <p className="text-sm mb-3"><strong>When to use:</strong> The user asks about something they've read, saved, or bookmarked.</p>
            <ParamTable params={[
              { name: 'query', type: 'string', required: true, desc: 'Search keywords' },
              { name: 'limit', type: 'number', required: false, default: '10', desc: 'Max results (1–50)' },
            ]} />
            <div className="mt-3">
              <CopyBlock code={`// Returns
[{
  "id": "uuid",
  "title": "Page Title",
  "url": "https://example.com/article",
  "snippet": "...matching text excerpt...",
  "notes_preview": "User's notes...",
  "created_at": "2026-03-05T10:30:00Z",
  "site_name": "Example Blog"
}]`} language="json" />
            </div>
          </div>

          {/* LIST_RECENT */}
          <div id="list_recent" className="mb-12 scroll-mt-20">
            <h3 className="text-xl font-semibold mb-1 font-mono">list_recent</h3>
            <p className="text-muted-foreground mb-4">
              List most recently saved pages, newest first.
            </p>
            <p className="text-sm mb-3"><strong>When to use:</strong> "What did I save recently?", browsing without a specific query.</p>
            <ParamTable params={[
              { name: 'limit', type: 'number', required: false, default: '10', desc: 'Number of pages (1–50)' },
            ]} />
          </div>

          {/* GET */}
          <div id="get" className="mb-12 scroll-mt-20">
            <h3 className="text-xl font-semibold mb-1 font-mono">get</h3>
            <p className="text-muted-foreground mb-4">
              Retrieve the <strong>full content</strong> of a saved page by UUID.
            </p>
            <p className="text-sm mb-3"><strong>When to use:</strong> After finding a page via search/list_recent, use get to read its full text.</p>
            <ParamTable params={[
              { name: 'id', type: 'string (UUID)', required: true, desc: "Page UUID from search/list results" },
            ]} />
            <div className="mt-3">
              <CopyBlock code={`// Returns
{
  "id": "uuid",
  "title": "Page Title",
  "url": "https://example.com/article",
  "content": "Full extracted text...",
  "description": "Meta description",
  "notes": "User's personal notes",
  "created_at": "2026-03-05T10:30:00Z",
  "published_at": "2026-03-01T08:00:00Z",
  "site_name": "Example Blog",
  "author": "Jane Doe",
  "type": "article",
  "canonical_url": "https://example.com/article",
  "check_later": false
}`} language="json" />
            </div>
          </div>

          {/* SAVE */}
          <div id="save" className="mb-12 scroll-mt-20">
            <h3 className="text-xl font-semibold mb-1 font-mono">save</h3>
            <p className="text-muted-foreground mb-4">
              Save a new URL. Fetches the page, extracts metadata and content, and stores it.
            </p>
            <p className="text-sm mb-3"><strong>When to use:</strong> User shares a URL to bookmark. <em>Always confirm before saving.</em></p>
            <ParamTable params={[
              { name: 'url', type: 'string (URL)', required: true, desc: 'URL to save' },
              { name: 'notes', type: 'string', required: false, desc: 'Optional notes to attach' },
            ]} />
          </div>

          {/* UPDATE */}
          <div id="update" className="mb-12 scroll-mt-20">
            <h3 className="text-xl font-semibold mb-1 font-mono">update</h3>
            <p className="text-muted-foreground mb-4">
              Update fields on an existing saved page. Supports partial updates, note appending, and optimistic concurrency.
            </p>
            <p className="text-sm mb-3"><strong>When to use:</strong> Edit notes, fix titles, add annotations, enrich a page with analysis.</p>
            <ParamTable params={[
              { name: 'id', type: 'string (UUID)', required: true, desc: "Page UUID" },
              { name: 'set', type: 'object', required: false, desc: 'Fields to overwrite (see below)' },
              { name: 'append_notes', type: 'string', required: false, desc: 'Text to append to notes (blank line separator)' },
              { name: 'expected_version', type: 'number', required: false, desc: 'Optimistic lock: only update if version matches' },
            ]} />
            <p className="text-sm mt-3 mb-2">At least one of <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">set</code> or <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">append_notes</code> is required.</p>

            <div className="overflow-x-auto mt-4">
              <p className="text-sm font-medium mb-2">Editable fields in <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">set</code>:</p>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium">Field</th>
                    <th className="text-left py-2 pr-4 font-medium">Type</th>
                    <th className="text-left py-2 font-medium">Max Length</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    ['title', 'string | null', '500'],
                    ['description', 'string | null', '5,000'],
                    ['notes', 'string | null', '50,000'],
                    ['content', 'string | null', '250,000'],
                    ['check_later', 'boolean', '—'],
                    ['published_at', 'string | null', 'ISO 8601'],
                    ['canonical_url', 'string | null', 'http/https URL'],
                    ['site_name', 'string | null', '—'],
                    ['author', 'string | null', '—'],
                    ['type', 'string | null', '—'],
                  ].map(([field, type, max]) => (
                    <tr key={field} className="border-b border-muted">
                      <td className="py-1.5 pr-4 font-mono text-primary">{field}</td>
                      <td className="py-1.5 pr-4 text-muted-foreground">{type}</td>
                      <td className="py-1.5 text-muted-foreground">{max}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-xl border bg-card p-4 mt-4 text-sm space-y-2">
              <p><strong>append_notes:</strong> If notes exist, appends with <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">\n\n</code> separator. If no notes, sets directly.</p>
              <p><strong>expected_version:</strong> Pass the page's current <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">version</code> to prevent overwriting concurrent edits. If mismatch → conflict error.</p>
            </div>
          </div>

          {/* DELETE */}
          <div id="delete" className="mb-12 scroll-mt-20">
            <h3 className="text-xl font-semibold mb-1 font-mono">delete</h3>
            <p className="text-muted-foreground mb-4">
              Permanently delete a saved page. <strong>Always confirm with the user first.</strong>
            </p>
            <ParamTable params={[
              { name: 'id', type: 'string (UUID)', required: true, desc: "Page UUID" },
            ]} />
          </div>
        </section>

        {/* ========== WORKFLOWS ========== */}
        <section id="workflows" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold mb-4">Workflows & Best Practices</h2>

          <div className="space-y-6">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold mb-2">Answering questions about saved content</h3>
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                <li><code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">search</code> with relevant keywords → get matching page IDs</li>
                <li><code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">get</code> the most relevant pages → read full content</li>
                <li>Synthesize an answer citing the source pages (title + URL)</li>
              </ol>
            </div>

            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold mb-2">Saving a page</h3>
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                <li>User shares a URL</li>
                <li>Confirm: "Want me to save this to your Solem library?"</li>
                <li><code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">save</code> with optional notes</li>
              </ol>
            </div>

            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold mb-2">Adding research notes</h3>
              <ol className="list-decimal pl-5 space-y-1 text-sm">
                <li><code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">search</code> / <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">list_recent</code> to find the page</li>
                <li><code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">get</code> to read current notes</li>
                <li><code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">update</code> with <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">append_notes</code> — never overwrite without asking</li>
              </ol>
            </div>

            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-semibold mb-3">Agent guidelines</h3>
              <ul className="space-y-1.5 text-sm">
                <li>📖 <strong>Read before write</strong> — always search/get before update/delete</li>
                <li>✅ <strong>Confirm before mutating</strong> — ask before save, destructive updates, or delete</li>
                <li>📎 <strong>Cite sources</strong> — mention page title and URL when answering</li>
                <li>🎯 <strong>Be efficient</strong> — use search with good keywords rather than listing everything</li>
                <li>🔒 <strong>Respect privacy</strong> — the knowledge base is personal</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ========== RATE LIMITS ========== */}
        <section id="rate-limits" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold mb-4">Rate Limits</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-medium">Scope</th>
                  <th className="text-left py-2 font-medium">Limit</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-muted">
                  <td className="py-2 pr-4">Per API key</td>
                  <td className="py-2">100 requests / minute</td>
                </tr>
                <tr className="border-b border-muted">
                  <td className="py-2 pr-4">Global (all keys)</td>
                  <td className="py-2">1,000 requests / minute</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* ========== ERRORS ========== */}
        <section id="errors" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold mb-4">Error Handling</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-medium">Error</th>
                  <th className="text-left py-2 font-medium">What to do</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Page not found or not accessible', 'Check UUID from search/list results'],
                  ['No pages found matching "..."', 'Try different keywords'],
                  ['Conflict: expected_version mismatch', 'Re-read page, get new version, retry'],
                  ['At least one mutation is required', 'Include set or append_notes in update'],
                  ['Unknown fields in set', 'Only use documented editable fields'],
                  ['429 Too Many Requests', 'Wait a few seconds and retry'],
                  ['401 Unauthorized', 'Check API key is correct and not revoked'],
                ].map(([err, fix]) => (
                  <tr key={err} className="border-b border-muted">
                    <td className="py-2 pr-4 font-mono text-xs">{err}</td>
                    <td className="py-2 text-muted-foreground">{fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Footer */}
        <div className="border-t pt-6 text-sm text-muted-foreground">
          <p>Server: <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">solem v1.0.0</code> · Transport: Streamable HTTP · Health: <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">GET https://mcp.solem.ai/health</code></p>
        </div>
      </div>
    </div>
  );
}
